import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { getAttributionForServer, markAttributionConsumed } from '../utils/attribution';
import { devLog } from '../utils/devLogger';
import {
  saveRememberedProfile,
  clearRememberedProfile,
} from '../utils/rememberedProfile';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing auth session on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Sprint 181 — URL-fragment fallback rescue.
    // The /api/auth/admin-direct success page redirects to /admin#hibff_token=…
    // as a two-channel handoff in case the localStorage.setItem write was
    // silently suppressed (incognito strict mode, browser extensions,
    // partitioned storage). If we see that fragment on boot, persist the
    // token via apiClient and immediately strip the hash so the URL stays
    // clean and the token doesn't leak via history / referer.
    try {
      const hash = window.location.hash || '';
      const m = hash.match(/[#&]hibff_token=([^&]+)/);
      if (m && m[1]) {
        const recovered = decodeURIComponent(m[1]);
        apiClient.setToken(recovered, true);
        // Strip the fragment without triggering a reload.
        const clean = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', clean);
      }
    } catch (e) {
      console.warn('hibff_token fragment rescue failed:', e);
    }

    const checkAuth = async () => {
      const token = apiClient.getToken();

      if (token) {
        try {
          // Validate token by fetching current user
          const userData = await apiClient.getCurrentUser();
          setUser(userData);
          // Sprint 207P — peer-invite acceptance also fires for users who
          // arrived via /join/:code → /register flow (Register.js doesn't
          // route through AuthContext.login). Idempotent at the backend.
          try {
            const pendingCode = localStorage.getItem('hibff_peer_invite_code');
            if (pendingCode) {
              apiClient
                .request('/api/peer-invites/accept', {
                  method: 'POST',
                  body: JSON.stringify({ code: pendingCode }),
                })
                .catch(() => {})
                .finally(() => {
                  try { localStorage.removeItem('hibff_peer_invite_code'); } catch {}
                });
            }
          } catch {}
        } catch (err) {
          // One retry with a small delay — absorbs the race between the
          // admin-direct two-channel handoff persisting the token and the
          // backend accepting it (cookie/JWT propagation, clock skew, etc).
          // This is the difference between "intermittent ghost-401" and
          // a real invalid-token state.
          try {
            await new Promise((r) => setTimeout(r, 350));
            const userData = await apiClient.getCurrentUser();
            setUser(userData);
          } catch (err2) {
            console.error('Auth check failed after retry:', err2);
            // Token is genuinely invalid — clear and proceed unauthenticated.
            apiClient.clearToken();
            localStorage.removeItem('hibff_user_cache');
            sessionStorage.removeItem('hibff_user_cache');
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    setError(null);

    try {
      const response = await apiClient.login(email, password);

      // Store token
      apiClient.setToken(response.access_token, rememberMe);

      // Store user data
      const userData = response.user;
      // Pull the full profile to pick up flags (is_demo, demo_region) not in
      // the compact login response.
      let fullUser = userData;
      try {
        fullUser = await apiClient.getCurrentUser();
      } catch {}
      setUser(fullUser);

      // Cache user data for quick access
      const userCache = JSON.stringify(fullUser);
      if (rememberMe) {
        localStorage.setItem('hibff_user_cache', userCache);
      } else {
        sessionStorage.setItem('hibff_user_cache', userCache);
      }

      // Returning-user welcome hint (first name only, opt-in via Remember me).
      // Privacy: no email / no ID / no avatar — see utils/rememberedProfile.js.
      saveRememberedProfile(fullUser, rememberMe);

      devLog(`✅ Login successful: ${fullUser.name} (${fullUser.email})`);

      // Sprint 207P — if the user landed via a peer-invite link
      // (PeerInviteLanding stashed the code in localStorage), bind the
      // peer relation now so the inviter can earn a Support Trophy on
      // this user's first challenge completion. Fire-and-forget.
      try {
        const pendingCode = localStorage.getItem('hibff_peer_invite_code');
        if (pendingCode) {
          apiClient
            .request('/api/peer-invites/accept', {
              method: 'POST',
              body: JSON.stringify({ code: pendingCode }),
            })
            .catch(() => {})
            .finally(() => {
              try { localStorage.removeItem('hibff_peer_invite_code'); } catch {}
            });
        }
      } catch {}

      // Attach first-touch UTM attribution to the user record (silent, no-op
      // if there's nothing captured or it's already attached server-side).
      try {
        const attrPayload = getAttributionForServer();
        if (attrPayload) {
          await apiClient.request('/api/users/utm', { method: 'POST', body: JSON.stringify(attrPayload) });
          markAttributionConsumed();
        }
      } catch (_) { /* never block login on attribution */ }

      return fullUser;
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);

      // Map API errors to user-friendly messages
      if (errorMessage.includes('Invalid email or password')) {
        throw new Error('WRONG_PASSWORD');
      }
      throw err;
    }
  };

  const register = async (userData, rememberMe = false) => {
    setError(null);

    try {
      // Format data for API. We forward the new Maya chat preferences so
      // the backend can persist them into `users.profile.*` for AvA
      // matching, first-message generation and meme prompts.
      const registrationData = {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        dob: userData.dob, // Should be YYYY-MM-DD format
        phone: userData.phone || null,
        parent_email: userData.parentEmail || null,
        parent_name: userData.parentName || null,
        parent_phone: userData.parentPhone || null,
        school_code: userData.schoolCode || null,
        favorite_color: userData.favorite_color || null,
        favorite_animal: userData.favorite_animal || null,
        favorite_place: userData.favorite_place || null,
        favorite_actor: userData.favorite_actor || null,
        favorite_sport: userData.favorite_sport || null,
        // Maya chat (12-question block)
        pronouns: userData.pronouns || null,
        main_goal: userData.main_goal || null,
        preferred_vibe: userData.preferred_vibe || null,
        top_hobby: userData.top_hobby || null,
        music_genre: userData.music_genre || null,
        favorite_movie: userData.favorite_movie || null,
        spirit_animal: userData.spirit_animal || null,
        happy_place: userData.happy_place || null,
        superpower: userData.superpower || null,
        demo_mode: !!userData.demo_mode,
        demo_suppress_celebrations: !!userData.demo_suppress_celebrations,
      };

      const response = await apiClient.register(registrationData);

      // Store token
      apiClient.setToken(response.access_token, rememberMe);

      // Store user data
      const user = response.user;
      setUser(user);

      // Cache user data
      const userCache = JSON.stringify(user);
      if (rememberMe) {
        localStorage.setItem('hibff_user_cache', userCache);
      } else {
        sessionStorage.setItem('hibff_user_cache', userCache);
      }

      // Returning-user welcome hint (first name only, opt-in via Remember me).
      saveRememberedProfile(user, rememberMe);

      devLog(`✅ Registration successful: ${user.name} (${user.email})`);

      // Attach first-touch UTM attribution to the new user record
      try {
        const attrPayload = getAttributionForServer();
        if (attrPayload) {
          await apiClient.request('/api/users/utm', { method: 'POST', body: JSON.stringify(attrPayload) });
          markAttributionConsumed();
        }
      } catch (_) { /* never block registration on attribution */ }

      // For teens, they may need to wait for parent setup
      if (user.parent_setup_complete === false) {
        // Return user but indicate parent setup is needed
        return { ...user, needsParentSetup: true };
      }

      return user;
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);

      if (errorMessage.includes('already registered')) {
        throw new Error('EMAIL_EXISTS');
      }
      if (errorMessage.includes('13 years old')) {
        throw new Error('AGE_REQUIREMENT');
      }
      if (errorMessage.includes('COPPA')) {
        throw new Error('PARENT_REQUIRED');
      }
      throw err;
    }
  };

  const verifyEmail = async (email, code) => {
    // Email verification is auto-done for adults in current implementation
    // This is a placeholder for future email verification flow
    devLog(`Email verification requested for ${email} with code ${code}`);
    return true;
  };

  const logout = useCallback(() => {
    devLog(`👋 Logout: ${user?.name || 'User'}`);

    // Clear token
    apiClient.clearToken();

    // Clear cached data
    localStorage.removeItem('hibff_user_cache');
    sessionStorage.removeItem('hibff_user_cache');
    localStorage.removeItem('hibff_last_activity');

    // Explicit sign-out / lock / idle-timeout also wipes the welcome-back
    // hint so a shared device returns to the neutral "Welcome Back" copy.
    clearRememberedProfile();

    // Clear state
    setUser(null);
    setError(null);
  }, [user?.name]);

  // ── S-034: Session security — 2-hour idle timeout + lock button + tab visibility check
  useEffect(() => {
    if (!user) return;

    const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
    let idleTimer;

    const recordActivity = () => {
      localStorage.setItem('hibff_last_activity', Date.now().toString());
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // Idle 2hr — force re-login
        devLog('Session idle 2h — locking');
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    // Cross-tab: if another tab signed out, this tab follows
    const handleStorage = (e) => {
      if (e.key === 'hibff_force_lock' && e.newValue === '1') {
        logout();
      }
      // Tab-coming-back-after-2h check
      if (e.key === 'hibff_last_activity' && e.newValue) {
        const last = parseInt(e.newValue, 10);
        if (Date.now() - last > IDLE_TIMEOUT_MS) {
          logout();
        }
      }
    };

    // Tab visibility — if user comes back after 2h, force re-login
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const last = parseInt(localStorage.getItem('hibff_last_activity') || '0', 10);
        if (last && Date.now() - last > IDLE_TIMEOUT_MS) {
          logout();
        } else {
          recordActivity();
        }
      }
    };

    ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'].forEach(ev =>
      document.addEventListener(ev, recordActivity, { passive: true })
    );
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibility);
    recordActivity();

    return () => {
      clearTimeout(idleTimer);
      ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'].forEach(ev =>
        document.removeEventListener(ev, recordActivity)
      );
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, logout]);

  // Lock-now action (Lock button on top nav broadcasts to all tabs)
  const lockSession = useCallback(() => {
    localStorage.setItem('hibff_force_lock', '1');
    setTimeout(() => localStorage.removeItem('hibff_force_lock'), 1000);
    logout();
  }, [logout]);

  const socialLogin = async (provider, mockUserData, rememberMe = false) => {
    // Social login is not yet implemented in the API
    // This is a placeholder for future OAuth integration
    devLog(`🔐 ${provider} OAuth - Feature coming soon`);
    throw new Error('SOCIAL_LOGIN_NOT_IMPLEMENTED');
  };

  const completeSocialSignup = async (socialUserData, dob, age, rememberMe = false) => {
    // Placeholder for social signup completion
    throw new Error('SOCIAL_LOGIN_NOT_IMPLEMENTED');
  };

  const updateUser = async (updates) => {
    try {
      const updatedUser = await apiClient.updateProfile(updates);
      setUser(updatedUser);

      // Update cache
      const persistentCache = localStorage.getItem('hibff_user_cache');
      const sessionCache = sessionStorage.getItem('hibff_user_cache');

      if (persistentCache) {
        localStorage.setItem('hibff_user_cache', JSON.stringify(updatedUser));
      }
      if (sessionCache) {
        sessionStorage.setItem('hibff_user_cache', JSON.stringify(updatedUser));
      }

      return updatedUser;
    } catch (err) {
      console.error('Failed to update user:', err);
      throw err;
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await apiClient.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      error,
      login,
      register,
      verifyEmail,
      socialLogin,
      completeSocialSignup,
      logout,
      lockSession,
      updateUser,
      refreshUser,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
